'use client'

import Link from 'next/link'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Shared shells for chat tool result cards.
 *
 * These follow DESIGN.md §Cards → "Compact variant — chat surface only": `p-4`
 * containers, `px-4 py-3` rows, `text-sm` titles, `text-xs` metadata — but the
 * `border border-border` + `shadow-sm` surface strategy is kept intact.
 *
 * Multi-result lists are one card with `divide-y` rows rather than a grid of
 * cards, matching the editorial list pattern from §Dashboard Hierarchy.
 */

/** Small uppercase eyebrow above a result block. DESIGN.md §Heading Hierarchy. */
export function ResultLabel({ children }: { children: ReactNode }) {
    return (
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {children}
        </p>
    )
}

export function ResultBlock({
    label,
    children,
    className,
}: {
    label?: ReactNode
    children: ReactNode
    className?: string
}) {
    return (
        <section className={cn('flex flex-col gap-2', className)}>
            {label && <ResultLabel>{label}</ResultLabel>}
            {children}
        </section>
    )
}

/** Bounded surface: border + shadow per DESIGN.md §Surface Strategy, compact padding. */
export function CompactCard({
    children,
    className,
}: {
    children: ReactNode
    className?: string
}) {
    return (
        <div
            data-testid="chat-tool-card"
            className={cn(
                'rounded-lg border border-border bg-card p-4 shadow-sm',
                className
            )}
        >
            {children}
        </div>
    )
}

/** Container for divider-separated rows. Rows supply their own padding. */
export function RowList({
    children,
    className,
}: {
    children: ReactNode
    className?: string
}) {
    return (
        <div
            data-testid="chat-tool-card"
            className={cn(
                'divide-y divide-border overflow-hidden rounded-lg border border-border bg-card shadow-sm',
                className
            )}
        >
            {children}
        </div>
    )
}

/**
 * One row in a RowList. Renders as a Link when `href` is set so chat results are
 * a real entry point into the app rather than a dead-end summary.
 * `min-h-11` satisfies DESIGN.md §Touch Targets.
 */
export function Row({
    href,
    children,
    className,
}: {
    href?: string
    children: ReactNode
    className?: string
}) {
    const body = (
        <div
            className={cn(
                'flex min-h-11 items-center gap-3 px-4 py-3 text-left',
                href && 'transition-colors hover:bg-muted',
                className
            )}
        >
            {children}
        </div>
    )

    if (!href) return body

    return (
        <Link
            href={href}
            className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
        >
            {body}
        </Link>
    )
}

export function RowTitle({ children }: { children: ReactNode }) {
    return <p className="truncate text-sm font-medium text-foreground">{children}</p>
}

/**
 * Title link inside a detail card.
 *
 * Must be `next/link`, never a bare `<a>`: a plain anchor triggers a full
 * document load, which tears down the React tree — and with it the in-memory
 * chat transcript. Client-side navigation at least keeps the FAB conversation
 * alive across the jump.
 */
export function CardTitleLink({ href, children }: { href?: string; children: ReactNode }) {
    if (!href) return <span className="text-sm font-medium text-foreground">{children}</span>
    return (
        <Link
            href={href}
            className="text-sm font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
            {children}
        </Link>
    )
}

/** Pill-shaped link for related-entity chips. Same soft-navigation requirement. */
export function PillLink({ href, children }: { href: string; children: ReactNode }) {
    return (
        <Link href={href} className="focus-visible:outline-none">
            <StatusPill tone="neutral" className="hover:bg-secondary">
                {children}
            </StatusPill>
        </Link>
    )
}

export function RowMeta({ children }: { children: ReactNode }) {
    if (!children) return null
    return <p className="truncate text-xs text-muted-foreground">{children}</p>
}

type PillTone = 'neutral' | 'success' | 'warning' | 'destructive' | 'info'

const PILL_TONES: Record<PillTone, string> = {
    // Tinted-bg pattern from DESIGN.md §Alerts & Status Badges: use the base
    // `text-{status}` color, never `text-{status}-foreground`, on a tinted bg.
    neutral: 'bg-muted text-muted-foreground border-border',
    success: 'bg-success/12 text-success border-success/20',
    warning: 'bg-warning/15 text-warning border-warning/20',
    destructive: 'bg-destructive/12 text-destructive border-destructive/20',
    info: 'bg-info/12 text-info border-info/20',
}

export function StatusPill({
    tone = 'neutral',
    children,
    className,
}: {
    tone?: PillTone
    children: ReactNode
    className?: string
}) {
    return (
        <span
            className={cn(
                'inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
                PILL_TONES[tone],
                className
            )}
        >
            {children}
        </span>
    )
}

/** Shown when a read tool succeeds but matched nothing. */
export function EmptyResult({ children }: { children: ReactNode }) {
    return (
        <CompactCard className="border-dashed shadow-none">
            <p className="text-sm text-muted-foreground">{children}</p>
        </CompactCard>
    )
}

/**
 * Trailing "and N more" affordance for truncated lists. Tools cap their result
 * sets (10–50 rows), so the card must not imply it is showing everything.
 */
export function MoreRow({ count, href }: { count: number; href: string }) {
    if (count <= 0) return null
    return (
        <Row href={href}>
            <p className="text-xs font-medium text-primary">
                View {count} more {count === 1 ? 'result' : 'results'}
            </p>
        </Row>
    )
}
